import React, { useState, useEffect, useMemo } from 'react';
import { parseAssetModel, getBatterySlaveIds, BATTERY_TYPE } from '../utils/assetConfig';
import { unionParameters, getParamAddress } from '../utils/bmsConstants';
import { getDefaultTimeRange } from '../utils/dateRangeDefaults';
import { API_BASE } from '../utils/api';
import './AddWidgetModal.css';


const TOTAL_STEPS = 5;

/**
 * AddWidgetModal
 *
 * Step 2 – Device selection:
 *   • All roles: checkboxes for the devices already in `devices` prop.
 *   • Role 2 only: "Browse other group" – pick any group the admin can
 *     access (fetched from getGroups) to pull its batteries into the pool,
 *     and "Add device by ID" – search every accessible group's vehicle list
 *     for a matching deviceId. Both rely on getGroupVehicles for the real
 *     vehicleId/carProfile, because getDeviceStatus (live status) doesn't
 *     return vehicle metadata.
 *
 * Extra devices fetched during this modal session are kept in `extraDevices`
 * state and are merged with the `devices` prop for all downstream steps.
 */
function AddWidgetModal({ isEdit, existingWidget, devices, authData, onSave, onCancel }) {
  const defaults = getDefaultTimeRange();
  const [step, setStep] = useState(1);
  const [title, setTitle] = useState(existingWidget?.title || '');
  const [selectedDevices, setSelectedDevices] = useState(existingWidget?.config?.selectedDevices || []);
  const [startDate, setStartDate] = useState(existingWidget?.config?.dateRange?.startDate || defaults.start);
  const [endDate, setEndDate] = useState(existingWidget?.config?.dateRange?.endDate || defaults.end);
  const [selectedBatteries, setSelectedBatteries] = useState(existingWidget?.config?.selectedBatteries || []);
  const [selectedParameters, setSelectedParameters] = useState(existingWidget?.config?.selectedParameters || []);

  // ── Extra device pool: seeded from saved config on edit, then extended ─────
  const [extraDevices, setExtraDevices] = useState(existingWidget?.config?.extraDevices || []);

  // All devices visible in step 2 (assigned + extras fetched this session)
  const allAvailableDevices = [
    ...devices,
    ...extraDevices.filter(ed => !devices.some(d => d.deviceId === ed.deviceId))
  ];

  // ── Role-2 group browser state ─────────────────────────────────────────────
  const [allGroups, setAllGroups] = useState([]);
  const [loadingAllGroups, setLoadingAllGroups] = useState(false);
  const [allGroupsError, setAllGroupsError] = useState(null);
  const [groupSearchQuery, setGroupSearchQuery] = useState('');
  const [expandedGroupId, setExpandedGroupId] = useState(null);
  // groupId -> vehicles[] once fetched, or an error message string
  const [groupVehiclesCache, setGroupVehiclesCache] = useState({});
  const [loadingGroupVehicles, setLoadingGroupVehicles] = useState(null); // groupId currently loading

  const isRole2 = authData?.role === 2;

  // Fetch every group the admin can access, so "choose any group" doesn't
  // require already knowing a raw Group ID.
  useEffect(() => {
    if (!isRole2 || !authData?.token) return;
    let cancelled = false;
    setLoadingAllGroups(true);
    setAllGroupsError(null);
    (async () => {
      try {
        const url = `${API_BASE}/api/admin/getGroups?api_access_token=${authData.token}`;
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) setAllGroups(data.groups || []);
      } catch (err) {
        if (!cancelled) setAllGroupsError(err.message);
      } finally {
        if (!cancelled) setLoadingAllGroups(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isRole2, authData]);

  const filteredGroups = useMemo(() => {
    const q = groupSearchQuery.trim().toLowerCase();
    if (!q) return allGroups;
    return allGroups.filter(g => g.groupName?.toLowerCase().includes(q));
  }, [allGroups, groupSearchQuery]);

  const addVehiclesToPool = (vehicles) => {
    setExtraDevices(prev => {
      const merged = [...prev];
      vehicles.forEach(v => {
        if (!merged.some(d => d.deviceId === v.deviceId) && !devices.some(d => d.deviceId === v.deviceId)) {
          merged.push({
            deviceId: v.deviceId,
            vehicleId: v.vehicleId,
            _id: v._id,
            carProfile: v.carProfile || null
          });
        }
      });
      return merged;
    });
  };

  // Fetch a group's vehicles (real carProfile/vehicleId, unlike getDeviceStatus)
  // and merge them into the pool so they show up as checkboxes above.
  const fetchGroupVehicles = async (groupId) => {
    if (Array.isArray(groupVehiclesCache[groupId])) return groupVehiclesCache[groupId];
    setLoadingGroupVehicles(groupId);
    try {
      const url = `${API_BASE}/api/admin/getGroupVehicles?groupId=${groupId}&api_access_token=${authData.token}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const vehicles = data.vehicles || [];
      setGroupVehiclesCache(prev => ({ ...prev, [groupId]: vehicles }));
      return vehicles;
    } catch (err) {
      setGroupVehiclesCache(prev => ({ ...prev, [groupId]: err.message }));
      return [];
    } finally {
      setLoadingGroupVehicles(null);
    }
  };

  const handleToggleGroup = async (group) => {
    if (expandedGroupId === group._id) {
      setExpandedGroupId(null);
      return;
    }
    setExpandedGroupId(group._id);
    const vehicles = await fetchGroupVehicles(group._id);
    if (Array.isArray(vehicles) && vehicles.length > 0) {
      addVehiclesToPool(vehicles);
    }
  };

  // ── Derived slave list for step 4 ─────────────────────────────────────────
  const getAvailableSlaves = () => {
    const list = [];
    selectedDevices.forEach(dId => {
      const dev = allAvailableDevices.find(d => d.deviceId === dId);
      if (!dev) return;
      const assetConfig = parseAssetModel(dev.carProfile?.model);
      const isDaly = assetConfig.type === BATTERY_TYPE.DALYLV;
      getBatterySlaveIds(assetConfig).forEach(slaveId => {
        list.push({
          deviceId: dId,
          slaveId,
          type: assetConfig.type,
          name: isDaly
            ? `DALY Pack (Slave ${slaveId}) (${dev.vehicleId || dId})`
            : `Slave ID ${slaveId} (${dev.vehicleId || dId})`
        });
      });
    });
    return list;
  };

  const selectedBatteryTypes = useMemo(() => {
    const types = selectedBatteries.map(b => b.type).filter(Boolean);
    return types.length > 0 ? [...new Set(types)] : [BATTERY_TYPE.GCE];
  }, [selectedBatteries]);

  const availableParameters = useMemo(() => {
    return unionParameters(selectedBatteryTypes);
  }, [selectedBatteryTypes]);

  const canAdvance = () => {
    if (step === 1) return title.trim().length > 0;
    if (step === 2) return selectedDevices.length > 0;
    if (step === 4) return selectedBatteries.length > 0;
    return true;
  };

  const handleNext = () => setStep(prev => prev + 1);
  const handlePrev = () => setStep(prev => prev - 1);

  const handleDeviceToggle = (deviceId, checked) => {
    setSelectedDevices(prev => checked ? [...prev, deviceId] : prev.filter(id => id !== deviceId));
    if (!checked) {
      setSelectedBatteries(prev => prev.filter(b => b.deviceId !== deviceId));
    }
  };

  const handleSave = () => {
    const config = {
      selectedDevices,
      dateRange: { startDate, endDate },
      selectedBatteries,
      selectedParameters,
      // Persist extra devices so the widget knows the carProfile/model later
      extraDevices: extraDevices.filter(ed => selectedDevices.includes(ed.deviceId))
    };
    onSave(title, config);
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h3>{isEdit ? 'Edit Widget' : 'Add Widget'} — Step {step} of {TOTAL_STEPS}</h3>

        <div className="modal-body">

          {/* ── Step 1: Title ─────────────────────────────────────────────── */}
          {step === 1 && (
            <div className="form-group">
              <label htmlFor="widget-title">Widget Name</label>
              <input
                id="widget-title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Site A – SOC & Voltage"
                required
              />
            </div>
          )}

          {/* ── Step 2: Device selection ───────────────────────────────────── */}
          {step === 2 && (
            <div className="form-group">
              <label>Select Vehicles / Devices</label>

              {/* Assigned / already-fetched devices */}
              {allAvailableDevices.length > 0 ? (
                <div className="checkbox-list">
                  {allAvailableDevices.map(d => (
                    <div key={d.deviceId} className="aw-device-row">
                      <label>
                        <input
                          type="checkbox"
                          checked={selectedDevices.includes(d.deviceId)}
                          onChange={(e) => handleDeviceToggle(d.deviceId, e.target.checked)}
                        />
                        {d.vehicleId || d.deviceId}
                      </label>
                      {/* Kept outside the <label> so it doesn't get pulled into
                          the checkbox's accessible name / label text. */}
                      {extraDevices.some(ed => ed.deviceId === d.deviceId) && (
                        <span className="aw-extra-badge">added</span>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="field-hint">No assigned devices. Use the lookup tools below.</p>
              )}

              {/* ── Role-2: browse any group ──────────────────────────────── */}
              {isRole2 && (
                <div className="aw-lookup-section">
                  <div className="aw-lookup-label">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                    </svg>
                    Browse other group
                  </div>
                  <input
                    id="aw-group-search-input"
                    type="text"
                    placeholder={loadingAllGroups ? 'Loading groups…' : `Search ${allGroups.length} group${allGroups.length !== 1 ? 's' : ''}…`}
                    value={groupSearchQuery}
                    onChange={e => setGroupSearchQuery(e.target.value)}
                    disabled={loadingAllGroups}
                  />
                  {allGroupsError && (
                    <p className="aw-lookup-error">Failed to load groups: {allGroupsError}</p>
                  )}
                  {!loadingAllGroups && !allGroupsError && (
                    <div className="aw-group-list">
                      {filteredGroups.map(group => {
                        const isOpen = expandedGroupId === group._id;
                        const groupVehicles = groupVehiclesCache[group._id];
                        return (
                          <div key={group._id} className="aw-group-item">
                            <button
                              type="button"
                              className="aw-group-header"
                              onClick={() => handleToggleGroup(group)}
                            >
                              <span>{group.groupName}</span>
                              <span className="aw-chevron">{isOpen ? '▾' : '▸'}</span>
                            </button>
                            {isOpen && (
                              <p className="aw-group-detail">
                                {loadingGroupVehicles === group._id
                                  ? 'Loading batteries…'
                                  : typeof groupVehicles === 'string'
                                    ? `Error: ${groupVehicles}`
                                    : Array.isArray(groupVehicles)
                                      ? groupVehicles.length === 0
                                        ? 'No batteries in this group.'
                                        : `${groupVehicles.length} device${groupVehicles.length !== 1 ? 's' : ''} added to the list above — check the ones you want.`
                                      : ''}
                              </p>
                            )}
                          </div>
                        );
                      })}
                      {filteredGroups.length === 0 && (
                        <p className="field-hint">No groups matching "{groupSearchQuery}".</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Step 3: Date range ────────────────────────────────────────── */}
          {step === 3 && (
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="widget-start-date">Start Date/Time</label>
                <input
                  id="widget-start-date"
                  type="datetime-local"
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label htmlFor="widget-end-date">End Date/Time</label>
                <input
                  id="widget-end-date"
                  type="datetime-local"
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* ── Step 4: Battery slaves ────────────────────────────────────── */}
          {step === 4 && (
            <div className="form-group">
              <label>Select Battery Slaves</label>
              <p className="field-hint">
                At least one battery slave is required — it determines which telemetry series get charted.
              </p>
              <div className="checkbox-list">
                {getAvailableSlaves().map(slave => {
                  const isChecked = selectedBatteries.some(
                    b => b.deviceId === slave.deviceId && b.slaveId === slave.slaveId
                  );
                  return (
                    <label key={`${slave.deviceId}_${slave.slaveId}`}>
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) => {
                          setSelectedBatteries(prev => e.target.checked
                            ? [...prev, slave]
                            : prev.filter(b => !(b.deviceId === slave.deviceId && b.slaveId === slave.slaveId)));
                        }}
                      />
                      {slave.name}
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Step 5: Parameters ───────────────────────────────────────── */}
          {step === 5 && (
            <div className="form-group">
              <label>Parameters to Graph</label>
              <div className="checkbox-list">
                {availableParameters.map(p => {
                  const hasDaly = selectedBatteries.some(b => b.type === BATTERY_TYPE.DALYLV);
                  const isUnavailableOnDaly = hasDaly && getParamAddress(p.key, BATTERY_TYPE.DALYLV) === undefined;
                  return (
                    <label key={p.key}>
                      <input
                        type="checkbox"
                        checked={selectedParameters.some(param => param.key === p.key)}
                        onChange={(e) => {
                          setSelectedParameters(prev => e.target.checked
                            ? [...prev, p]
                            : prev.filter(param => param.key !== p.key));
                        }}
                      />
                      {p.name}
                      {isUnavailableOnDaly && (
                        <span style={{ fontSize: '0.8rem', color: '#8892b0', marginLeft: '0.5rem' }}>
                          (not available on DALY)
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>


        <div className="modal-footer">
          {step > 1 && <button className="compare-btn" onClick={handlePrev}>Back</button>}
          {step < TOTAL_STEPS ? (
            <button className="compare-btn primary" onClick={handleNext} disabled={!canAdvance()}>Next</button>
          ) : (
            <button
              className="compare-btn primary"
              onClick={handleSave}
              disabled={selectedParameters.length === 0}
            >
              Save
            </button>
          )}
          <button className="compare-btn cancel" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

export default AddWidgetModal;
